import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'

import Layout from '../components/Layout/Layout'
import PrintIcon from '../images/icons/print.svg'
import ContactIcon from '../images/icons/contact.svg'
import ShareIcon from '../images/icons/share.svg'

import pxToRem from '../utils/pxToRem'
import { HeadingSizeEnum } from '../components/Heading/Heading'
import { deviceMax } from '../theme/breakpoints'
import theme from '../theme/theme-style'
import Hero from '../components/Hero/Hero'
import CKEditorContent from '../components/CKEditorContent/CKEditorContent'
import { formatDate } from '../utils/FormatDate'
import printPage from '../utils/PrintPage'
import { sendEmailWithLink } from '../utils/SendEmail'
import ShareOverlay from '../components/ShareOverlay/ShareOverlay'
import ContentType from '../enums/ContentType'
import Carousel, { CarouselVariantEnum } from '../components/Carousel/Carousel'
import SlideDocument from '../components/Carousel/SlideDocument/SlideDocument'
import Seo from '../components/Seo/Seo'
import { TYPES_ENUM } from '../config/BreadCrumbsConfig'
import ChipVariantEnum from '../enums/ChipVariant'
import NotFoundPage from '../pages/404'
import useMediaQuery from '../hooks/useMediaQuery'
import {
  MOBILE_TITLE,
  StyledArticleCategories,
  StyledArticleContainer,
  StyledArticleContent,
  StyledArticleDate,
  StyledArticleDateCategories,
  StyledArticleElements,
  StyledArticleInfos,
  StyledCardContact,
  StyledChip,
  StyledContacts,
  StyledDate,
  StyledDecoration,
  StyledHeading,
  StyledIcon,
  StyledIconsContainer,
  StyledSeparator,
  StyledShareIcon,
} from './article.styled'

const Article = ({data, location}) => {
  if (data.contentfulTemplateArticle === null) {
    return <NotFoundPage location={location}/>
  }

  const {
    articleHtmlContent,
    backgroundColor,
    contacts,
    date,
    categories,
    imageHeaderDesktop,
    imageHeaderMobile,
    slug,
    title,
    documentsBlocDisplay,
    documentsBlocTitle,
    documentsList,
    seoMetaDescription,
  } = data.contentfulTemplateArticle

  const [isSharing, setIsSharing] = useState(false)
  const [dateFormated, setFormatedDate] = useState(null)
  const isMobile = useMediaQuery(deviceMax.mobileL)
  const [slidesDocument] = useState(
    SlideDocument(documentsList, documentsBlocDisplay)
  )

  useEffect(() => {
    setFormatedDate(formatDate(date))
  }, [])

  const ToggleSendEmail = () => sendEmailWithLink()
  const TogglePrint = () => printPage()
  const ToggleShareOverlay = () => setIsSharing(!isSharing)

  const htmlContent = articleHtmlContent ? articleHtmlContent.content : ''

  const contactsCards =
    contacts &&
    contacts.map((contact) => {
      return (
        <StyledCardContact
          department={contact.department}
          email={contact.email}
          firstname={contact.firstname}
          key={contact.id}
          lastname={contact.lastname}
          phone={contact.phone}
        />
      )
    })

  const categoriesChips =
    categories &&
    categories.map((element) => {
      return (
        <StyledChip label={element.name} variant={ChipVariantEnum.primary}/>
      )
    })

  return (
    <Layout>
      <Seo
        title={title}
        description={seoMetaDescription?.seoMetaDescription}
        canonicalHref={location.pathname}
      />
      <Hero
        backgroundColor={backgroundColor}
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        pathname={location.pathname}
        slug={slug}
        title={isMobile ? MOBILE_TITLE : title}
        type={TYPES_ENUM.article}
      />
      {isSharing && (
        <ShareOverlay
          title={isMobile ? MOBILE_TITLE : title}
          setIsSharing={setIsSharing}
          contentType={ContentType.article}
        />
      )}
      <StyledArticleContainer>
        <StyledArticleContent>
          {isMobile ? (
            <StyledArticleInfos>
              <StyledArticleDateCategories>
                <StyledArticleDate>
                  <StyledDecoration $color="lime"/>
                  <StyledDate>{dateFormated}</StyledDate>
                </StyledArticleDate>
                <StyledIconsContainer>
                  <StyledShareIcon
                    color={theme.colors.unique.transparent}
                    width={pxToRem(24)}
                    height={pxToRem(24)}
                    onClick={ToggleShareOverlay}
                  >
                    <ShareIcon/>
                  </StyledShareIcon>
                  <StyledIcon
                    color={theme.colors.unique.black}
                    width={pxToRem(24)}
                    height={pxToRem(24)}
                    onClick={ToggleSendEmail}
                  >
                    <ContactIcon/>
                  </StyledIcon>
                  {!isMobile && (
                    <StyledIcon
                      color={theme.colors.unique.black}
                      width={pxToRem(24)}
                      height={pxToRem(24)}
                      onClick={TogglePrint}
                    >
                      <PrintIcon/>
                    </StyledIcon>
                  )}
                </StyledIconsContainer>
              </StyledArticleDateCategories>
              <StyledArticleCategories>
                {categories && <StyledDecoration $color="blue"/>}
                {categoriesChips}
              </StyledArticleCategories>
            </StyledArticleInfos>
          ) : (
            <StyledArticleInfos>
              <StyledArticleDateCategories>
                <StyledArticleDate>
                  <StyledDecoration $color="lime"/>
                  <StyledDate>{dateFormated}</StyledDate>
                </StyledArticleDate>
                <StyledArticleCategories>
                  {categories && <StyledDecoration $color="blue"/>}
                  {categoriesChips}
                </StyledArticleCategories>
              </StyledArticleDateCategories>

              <StyledIconsContainer>
                <StyledShareIcon
                  color={theme.colors.unique.transparent}
                  width={pxToRem(24)}
                  height={pxToRem(24)}
                  onClick={ToggleShareOverlay}
                >
                  <ShareIcon/>
                </StyledShareIcon>
                <StyledIcon
                  color={theme.colors.unique.black}
                  width={pxToRem(24)}
                  height={pxToRem(24)}
                  onClick={ToggleSendEmail}
                >
                  <ContactIcon/>
                </StyledIcon>
                {!isMobile && (
                  <StyledIcon
                    color={theme.colors.unique.black}
                    width={pxToRem(24)}
                    height={pxToRem(24)}
                    onClick={TogglePrint}
                  >
                    <PrintIcon/>
                  </StyledIcon>
                )}
              </StyledIconsContainer>
            </StyledArticleInfos>
          )}
          <StyledSeparator/>
          {isMobile && (
            <StyledHeading label={title} size={HeadingSizeEnum.h3}/>
          )}
          <>
            <CKEditorContent html={htmlContent}/>
          </>
        </StyledArticleContent>
        {(contactsCards || documentsList) && (
          <StyledArticleElements>
            <StyledContacts>{contactsCards}</StyledContacts>
            {slidesDocument && (
              <Carousel
                title={documentsBlocTitle ? documentsBlocTitle : undefined}
                variant={CarouselVariantEnum.document}
              >
                {slidesDocument}
              </Carousel>
            )}
          </StyledArticleElements>
        )}
      </StyledArticleContainer>
    </Layout>
  )
}

export default Article

export const query = graphql`
  query ArticleQuery($contentful_id: String!) {
    contentfulTemplateArticle(contentful_id: { eq: $contentful_id }) {
      id
      title
      seoMetaDescription {
        seoMetaDescription
      }
      slug
      date
      imageHeaderDesktop {
        ...imageHeaderFragment
      }
      imageHeaderMobile {
        ...imageHeaderFragment
      }
      categories {
        id
        name
      }
      backgroundColor
      articleHtmlContent {
        content
      }
      contacts {
          ...contactFragment
      }
      documentsBlocDisplay
      documentsBlocTitle
      documentsList {
        ...documentFragment
      }
    }
  }
`
